import { useEffect, useState, useCallback } from 'react'
import { onValue, ref, get } from 'firebase/database'
import { db, tref } from './firebase.js'

// Robust Realtime-Database value hook — the fix for the "fire-and-forget
// onValue" failure that left rosters/lists empty at the venue:
//   • error callback on the listener, so permission/network failures SURFACE
//     instead of being swallowed (the success callback silently never firing)
//   • explicit status: 'loading' | 'ready' | 'error' (so the UI can show a
//     spinner / error+Retry instead of a blank, unopenable control)
//   • an initial get() that primes the value so we don't wait solely on the
//     WebSocket connecting
//   • a timeout that AUTO-RETRIES a couple of times, then reports 'error'
//   • retry() for one-tap manual recovery
// Returns { data, status, error, retry }.
export function useDbValue(path, { timeoutMs = 7000, autoRetries = 2 } = {}) {
  const [data, setData] = useState(null)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState(null)
  const [nonce, setNonce] = useState(0)

  const retry = useCallback(() => {
    setError(null)
    setStatus('loading')
    setNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!path) { setData(null); setStatus('ready'); setError(null); return }
    let alive = true
    let attempt = 0
    let timer = null
    let off = null
    const r = tref(path)

    const clear = () => { if (timer) { clearTimeout(timer); timer = null } }

    const deliver = (val) => {
      if (!alive) return
      clear()
      setData(val)
      setError(null)
      setStatus('ready')
    }
    const fail = (err) => {
      if (!alive) return
      clear()
      if (attempt < autoRetries) {
        attempt += 1
        timer = setTimeout(() => { if (alive) start() }, 1200 * attempt)
      } else {
        // eslint-disable-next-line no-console
        console.warn('[useDbValue] load failed:', path, err)
        setError(err || new Error('Could not load data. Check your connection.'))
        setStatus('error')
      }
    }
    const start = () => {
      if (off) { off(); off = null }
      if (alive && status !== 'loading') setStatus('loading')
      timer = setTimeout(() => fail(new Error('Timed out loading data — check your connection.')), timeoutMs)
      // Prime with a one-shot read so we don't hang purely on the socket.
      get(r).then((snap) => deliver(snap.val())).catch(() => { /* listener may still resolve */ })
      off = onValue(r, (snap) => deliver(snap.val()), fail)
    }
    start()
    return () => { alive = false; clear(); if (off) off() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, timeoutMs, autoRetries])

  return { data, status, error, retry }
}
